"use client";

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/providers/AuthProvider";
import { logger } from "@/utils/logger";
import { getPublicImageUrl } from "@/utils/imageUtils";

interface OrderStatus {
  order: {
    id: number;
    status: string;
    totalAmount: number;
    createdAt: string;
  };
  ticket: {
    id: string;
    status: string;
    prompt: string;
    imageUrl: string | null;
    backImageUrl: string | null;
    price: number | null;
    dressLength: string | null;
    sleeveStyle: string | null;
    necklineStyle: string | null;
    waistlineStyle: string | null;
    silhouetteFit: string | null;
    fabricTexture: string | null;
    color: string | null;
    createdAt: string;
    updatedAt: string;
  };
  companyName: string | null;
  isGuestOrder: boolean;
}

export function TrackPageContent() {
  const searchParams = useSearchParams();
  const { user } = useAuth();

  const ticketIdParam = searchParams.get("id");
  const emailParam = searchParams.get("email");

  const [ticketId, setTicketId] = useState(ticketIdParam || "");
  const [email, setEmail] = useState(emailParam || "");
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoLoaded, setAutoLoaded] = useState(false);

  const handleTrackOrder = useCallback(async () => {
    if (!ticketId || !email) {
      setError("Enter your order ID and the email address used at checkout.");
      return;
    }

    setLoading(true);
    setError(null);
    setOrderStatus(null);

    try {
      const response = await fetch(
        `/api/guest/order-status?ticketId=${encodeURIComponent(ticketId)}&email=${encodeURIComponent(email)}`
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to find order");
        return;
      }

      setOrderStatus(data);

      // Update URL with params for sharing/bookmarking
      const newUrl = `/track?id=${ticketId}&email=${encodeURIComponent(email)}`;
      window.history.replaceState({}, "", newUrl);
    } catch (err) {
      logger.error(
        "Track order error",
        { component: "TrackOrderPage" },
        err instanceof Error ? err : new Error(String(err))
      );
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [email, ticketId]);

  // Auto-fetch if params are provided
  useEffect(() => {
    if (ticketIdParam && emailParam && !autoLoaded) {
      setAutoLoaded(true);
      handleTrackOrder();
    }
  }, [ticketIdParam, emailParam, autoLoaded, handleTrackOrder]);

  const getStatusSteps = () => {
    const steps = [
      { key: "approved", label: "Order confirmed", icon: "✓" },
      { key: "paid", label: "Payment received", icon: "💳" },
      { key: "in-production", label: "In production", icon: "🧵" },
      { key: "shipped", label: "Shipped", icon: "📦" },
      { key: "delivered", label: "Delivered", icon: "🎉" },
    ];

    const statusOrder = [
      "approved",
      "paid",
      "in-production",
      "shipped",
      "delivered",
    ];
    const currentIndex = statusOrder.indexOf(orderStatus?.ticket.status || "");

    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      current: index === currentIndex,
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-card border border-border py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Track your order
          </h1>
          <p className="text-muted-foreground">
            Enter your order details to see the current status of your custom
            dress.
          </p>
        </div>

        {/* Search Form */}
        {!orderStatus && (
          <div className="mb-8">
            <Card>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleTrackOrder();
                }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="track-order-id">Order ID</Label>
                  <Input
                    id="track-order-id"
                    name="order-id"
                    type="text"
                    autoComplete="off"
                    value={ticketId}
                    onChange={e => setTicketId(e.target.value)}
                    placeholder="Enter your order ID from the confirmation email"
                  />
                  <p className="text-xs text-muted-foreground">
                    Find this in your order-confirmation email. If it is
                    missing,
                    <a
                      href={`mailto:${process.env.NEXT_PUBLIC_BUSINESS_EMAIL || "support@build-a-dress.com"}`}
                      className="ml-1 underline underline-offset-2 hover:text-foreground"
                    >
                      contact support
                    </a>
                    .
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="track-email">Email address</Label>
                  <Input
                    id="track-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Email used for the order"
                  />
                </div>

                {error && (
                  <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
                    <p className="text-sm text-error">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="xl"
                  fullWidth
                  loading={loading}
                  disabled={loading || !ticketId || !email}
                >
                  Track order
                </Button>
              </form>
            </Card>
          </div>
        )}

        {/* Order Status Display */}
        {orderStatus && (
          <div className="space-y-6">
            {/* Back to search button */}
            <button
              onClick={() => {
                setOrderStatus(null);
                setError(null);
              }}
              className="text-primary hover:text-primary text-sm font-medium flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Track another order
            </button>

            {/* Status Progress */}
            <Card>
              <h2 className="text-lg font-semibold text-foreground mb-6">
                Order status
              </h2>
              <div className="relative">
                {getStatusSteps().map((step, index) => (
                  <div
                    key={step.key}
                    className="flex items-start mb-6 last:mb-0"
                  >
                    <div className="flex flex-col items-center mr-4">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                          step.completed
                            ? step.current
                              ? "bg-primary text-primary-foreground"
                              : "bg-[var(--success-muted)] text-success"
                            : "bg-border text-muted-foreground"
                        }`}
                      >
                        {step.completed ? step.icon : index + 1}
                      </div>
                      {index < getStatusSteps().length - 1 && (
                        <div
                          className={`w-0.5 h-12 ${
                            step.completed
                              ? "bg-[var(--success-muted)]"
                              : "bg-border"
                          }`}
                        />
                      )}
                    </div>
                    <div className="pt-2">
                      <p
                        className={`font-medium ${
                          step.current
                            ? "text-primary"
                            : step.completed
                              ? "text-foreground"
                              : "text-muted-foreground"
                        }`}
                      >
                        {step.label}
                      </p>
                      {step.current && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Current status
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Order Details */}
            <Card padding="none" overflowHidden>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {/* Image */}
                {orderStatus.ticket.imageUrl && (
                  <div className="relative aspect-3/4 bg-muted">
                    <Image
                      src={getPublicImageUrl(orderStatus.ticket.imageUrl)}
                      alt={orderStatus.ticket.prompt || "Custom dress"}
                      fill
                      priority
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 50vw"
                    />
                  </div>
                )}

                {/* Details */}
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {orderStatus.ticket.prompt || "Custom Dress Design"}
                    </h3>
                    {orderStatus.companyName && (
                      <p className="text-sm text-muted-foreground">
                        Made by: {orderStatus.companyName}
                      </p>
                    )}
                  </div>

                  <div className="border-t border-border pt-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Order ID</span>
                      <span className="font-mono text-sm text-foreground">
                        {orderStatus.ticket.id.substring(0, 8)}...
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Order date</span>
                      <span className="text-foreground">
                        {formatDate(orderStatus.order.createdAt)}
                      </span>
                    </div>
                    {orderStatus.order.totalAmount && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total</span>
                        <span className="font-semibold text-foreground">
                          {formatCurrency(orderStatus.order.totalAmount)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Dress Details */}
                  {(orderStatus.ticket.dressLength ||
                    orderStatus.ticket.sleeveStyle ||
                    orderStatus.ticket.necklineStyle) && (
                    <div className="border-t border-border pt-4">
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">
                        Design details
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {orderStatus.ticket.dressLength && (
                          <div>
                            <span className="text-muted-foreground">
                              Length:{" "}
                            </span>
                            <span className="text-foreground">
                              {orderStatus.ticket.dressLength}
                            </span>
                          </div>
                        )}
                        {orderStatus.ticket.sleeveStyle && (
                          <div>
                            <span className="text-muted-foreground">
                              Sleeves:{" "}
                            </span>
                            <span className="text-foreground">
                              {orderStatus.ticket.sleeveStyle}
                            </span>
                          </div>
                        )}
                        {orderStatus.ticket.necklineStyle && (
                          <div>
                            <span className="text-muted-foreground">
                              Neckline:{" "}
                            </span>
                            <span className="text-foreground">
                              {orderStatus.ticket.necklineStyle}
                            </span>
                          </div>
                        )}
                        {orderStatus.ticket.fabricTexture && (
                          <div>
                            <span className="text-muted-foreground">
                              Fabric:{" "}
                            </span>
                            <span className="text-foreground">
                              {orderStatus.ticket.fabricTexture}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Guest Account Creation CTA */}
            {orderStatus.isGuestOrder && !user && (
              <Card variant="primary-muted">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[var(--primary-muted)] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary">👤</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">
                      Create an account
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Save your order to an account for easier tracking, direct
                      communication with your manufacturer, and faster checkout
                      on future orders.
                    </p>
                    <Button asChild variant="primary" size="xl">
                      <Link href={`/login?email=${encodeURIComponent(email)}`}>
                        Create account
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Help Section */}
            <Card>
              <h3 className="font-semibold text-foreground mb-3">Need help?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                If you have questions about your order, please contact our
                support team.
              </p>
              <Button variant="secondary" size="xl" asChild>
                <Link href="/faq">Read FAQs</Link>
              </Button>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

export function TrackPageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-card border border-border">
      <LoadingSpinner size="lg" />
    </div>
  );
}
